import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { ValidationIssue } from '@decisions/server/DecisionsFramework/ValidationIssue';
import { BreakLevel } from '@decisions/server/DecisionsFramework/BreakLevel';
import { findContextChildByPath } from 'components/property-editors/utils/ProviderNodeUtils';
import { DecisionsValidationIssue } from '@decisions/server/DecisionsFramework/Design/Form';

export interface NodeValidationIssues {
  issues: ValidationIssue[];
  fatal: number;
  warning: number;
}

// TODO most of this is probably property grid specific and should be moved there... :(

export const GLOBAL_ISSUE_KEY = 'global';
export const REF_PROP_SEPARATOR = '\\';

export function getAllValidationIssues(
  nodes?: ContextProviderNode[] | IPropertyChild[] | ContextProviderNode | IPropertyChild | ContextProviderNode,
  global?: ValidationIssue[]
): NodeValidationIssues {
  if (!nodes) {
    return {
      issues: [],
      fatal: 0,
      warning: 0,
    };
  }

  let issues: ValidationIssue[] = [];
  let fatal = 0;
  let warning = 0;

  if (!Array.isArray(nodes)) {
    nodes = [nodes as ContextProviderNode];
  }

  function countIssue(issue: ValidationIssue) {
    fatal += issue.Level === BreakLevel.Fatal ? 1 : 0;
    warning += issue.Level === BreakLevel.Warning ? 1 : 0;
    issues.push(issue);
  }

  nodes.forEach((node: ContextProviderNode | IPropertyChild) => {
    let children = getAllValidationIssues(node.Children);
    if (Array.isArray(node.ValidationIssues)) {
      node.ValidationIssues.forEach(countIssue);
    }
    fatal += children.fatal;
    warning += children.warning;
    issues.push(...children.issues);
  });

  if (Array.isArray(global)) {
    // also push in any global validation issues to the count if provided:
    global.forEach(countIssue);
  }

  return {
    issues,
    fatal,
    warning,
  };
}

function hasBreakLevel(targetLevel: BreakLevel, levels: BreakLevel[]) {
  return levels.find(level => level === targetLevel) !== undefined;
}

function hasFatal(levels: BreakLevel[]) {
  return hasBreakLevel(BreakLevel.Fatal, levels);
}

function hasWarning(levels: BreakLevel[]) {
  return hasBreakLevel(BreakLevel.Warning, levels);
}

export function hasInvalidPathValidation(validationIssues: ValidationIssue[]) {
  return validationIssues?.filter(validation => validation.ValidationCode === 42)?.length > 0;
}

export function getChildValidationIssues(child: ContextProviderNode): ValidationIssue[] {
  return child?.ValidationIssues ? child.ValidationIssues : [];
}

export function getChildValidationReasons(child: ContextProviderNode): string[] {
  return getChildValidationIssues(child).map(issue => issue.Reason);
}

export interface ValidationSummary {
  reasons: string[];
  levels: BreakLevel[];
  worstLevel: BreakLevel;
}

export function getValidationSummary(issues: ValidationIssue[]): ValidationSummary {
  const reasons = issues.map(issue => issue.Reason);
  const levels = issues.map(issue => issue.Level);
  const worstLevel = hasFatal(levels) ? BreakLevel.Fatal : hasWarning(levels) ? BreakLevel.Warning : BreakLevel.Ignored;
  return { reasons, levels, worstLevel };
}

export function getEditorValidationSummary(editor: ContextProviderNode) {
  return getValidationSummary(getChildValidationIssues(editor));
}

export function getParentValidationIssues(parent: ContextProviderNode) {
  // TODO fix tsconfig so we can just use Array.flat
  if (parent && Array.isArray(parent.Children)) {
    const issues = parent.Children.map(getChildValidationIssues);
    const flattened = [].concat(...(issues as any)); // TS fail :(
    return flattened as ValidationIssue[];
  }
  return [];
}

export function getParentValidationSummary(parent: ContextProviderNode): ValidationSummary {
  return getValidationSummary(getParentValidationIssues(parent));
}

export function getValidationSummaryFromFormIssues(
  issues: DecisionsValidationIssue[] | undefined
): ValidationSummary | undefined {
  if (!issues || !issues.length) {
    return undefined;
  }
  const reasons = issues.map(issue => issue.Message);
  const levels = issues.map(issue => issue.BreakLevel);
  const worstLevel = hasFatal(levels) ? BreakLevel.Fatal : hasWarning(levels) ? BreakLevel.Warning : BreakLevel.Ignored;
  return { reasons, levels, worstLevel };
}

/**
 * Clear validations across an entire {@link ContextProviderNode} tree.
 *
 * TODO if time, there's some risk to double-chck here regarding React change detection,
 * because it's mutating the node objects, instead of copying them.
 *
 * @param node to clear, including all of its' children, recursively;
 */
export function clearValidations(node: ContextProviderNode) {
  node.ValidationIssues = [];
  if (Array.isArray(node.Children)) {
    node.Children.forEach(clearValidations);
  }
}

export function aggregateValidationsByRefProp(validationIssues?: ValidationIssue[]) {
  const validationMap = new Map<string, ValidationIssue[]>();
  if (!validationIssues) {
    return validationMap;
  }
  validationIssues.forEach(issue => {
    const { ReferenceProperty } = issue;
    let key: string = ReferenceProperty || GLOBAL_ISSUE_KEY;
    const current = validationMap.get(key) || [];
    validationMap.set(key, [...current, issue]);
  });
  return validationMap;
}

export function addValidationsByRefProp(node: ContextProviderNode, issueMap: Map<string, ValidationIssue[]>) {
  const map = new Map(issueMap);
  map.delete(GLOBAL_ISSUE_KEY); // we aren't concerned with iterating global here
  map.forEach((issues, refProp) => {
    const path = refProp.split(REF_PROP_SEPARATOR);
    const { child } = findContextChildByPath(path, node);
    if (child) {
      child.ValidationIssues = issues;
    } else {
      // If there was a reference path, but there's no match, add it to the global list.
      node.ValidationIssues = [...node.ValidationIssues, ...issues];
      // TODO is this by design, or do we log a warning?
      // it seems to be by design, per legacy code, which also tries to ignore things with no reference path (!?!?)
      // console.log(
      //   "No matching path for Validation Issues' Reference Property\n",
      //   'Adding to global validations list.',
      //   issues
      // );
    }
  });
}
